'use client'

import React, { useState, useEffect, useRef } from 'react'
import MegaNav from '@/src/components/commons/MegaNav/MegaNav'

import SearchPopup from '../commons/SearchPopup/SearchPopup'

// --- Styles for the fixed header when scrolling ---
const FIXED_HEADER_CLASSES =
  'fixed top-0 left-0 right-0 transform transition-transform duration-500 ease-in-out'
const HIDDEN_CLASS = '-translate-y-full' // slide header out of view
const VISIBLE_CLASS = 'translate-y-0' // slide header into view

export default function MainLayout({ children }: { children: React.ReactNode }) {
  const headerRef = useRef<HTMLElement>(null)
  const [isHeaderFixed, setIsHeaderFixed] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [headerHeight, setHeaderHeight] = useState(0)

  // measure header element height (due to dynamic announcement carousel height changing )
  const updateHeaderMeasurements = (element: HTMLElement) => {
    const height = element.offsetHeight
    setHeaderHeight(height)
    // set dynamic CSS variable on the header element for MegaNav to use
    element.style.setProperty('--header-height', `${height}px`)
  }

  useEffect(() => {
    const headerElement = headerRef.current
    if (!headerElement) return

    // initialize height measurement
    updateHeaderMeasurements(headerElement)

    // setup MutationObserver for AlertCarousel height change
    const observer = new MutationObserver(() => {
      updateHeaderMeasurements(headerElement)
    })

    // set observer to watch attributes and subtree for layout changes
    observer.observe(headerElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['style', 'class'], // watch for style or class changes that affect layout
    })

    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Use the live state for the current height
      const initialHeaderHeight = headerElement.offsetHeight

      // 1. check if the header should become fixed
      if (currentScrollY > initialHeaderHeight) {
        setIsHeaderFixed(true)
      } else {
        setIsHeaderFixed(false)
        setIsVisible(true) // always visible when scrolling back to the very top
      }

      // 2. add "Show on Scroll Down" logic
      if (isHeaderFixed) {
        if (currentScrollY > lastScrollY && currentScrollY > initialHeaderHeight + 100) {
          // scrolling down AND scrolled past initial header height + buffer hides header
          setIsVisible(false)
        } else if (currentScrollY < lastScrollY) {
          // scrolling up to show header
          setIsVisible(true)
        }
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', () => updateHeaderMeasurements(headerElement)) // update on resize

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', () => updateHeaderMeasurements(headerElement))
      observer.disconnect() // disconnect the observer on cleanup
    }
  }, [isHeaderFixed, lastScrollY])

  // changes in header classes based on fixed header conditions
  const headerClasses = [
    'shadow-md',
    isHeaderFixed ? FIXED_HEADER_CLASSES : 'relative z-10',
    isHeaderFixed && !isVisible ? HIDDEN_CLASS : VISIBLE_CLASS,
  ].join(' ')

  // Search PopUp Code
  const [searchOpen, setSearchOpen] = useState(false)

  return (
    <>
      <header ref={headerRef} className={`${headerClasses} z-10`}>
        {/* <AlertCarousel data={alertData} /> */}
        <MegaNav onSearchOpen={() => setSearchOpen(true)} />
      </header>
      {/* IMPORTANT: add a spacer to prevent content jump */}
      {/* spacer uses the measured dynamic height */}
      {isHeaderFixed && <div style={{ height: `${headerHeight}px` }} aria-hidden="true" />}
      {children}
      {searchOpen && <SearchPopup onClose={() => setSearchOpen(false)} />}
    </>
  )
}
